/**
 * Utility functions for getting theme colors at runtime
 * Used primarily for charting libraries that need actual color values
 */

/**
 * Gets the computed value of a CSS variable
 * @param variable - CSS variable name (e.g., '--primary-main')
 * @returns The computed color value
 */
export const getCssVar = (variable: string): string => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(variable)
    .trim();
};

/**
 * Gets common theme colors as actual values
 * Useful for chart libraries that can't use CSS variables
 */
export const getThemeColors = () => ({
  primary: getCssVar('--primary-main'),
  secondary: getCssVar('--secondary-main'),
  success: getCssVar('--success-main'),
  error: getCssVar('--error-main'),
  errorDark: getCssVar('--error-dark'),
  warning: getCssVar('--warning-main'),
  info: getCssVar('--info-main'),
  infoLight: getCssVar('--info-light'),
  textPrimary: getCssVar('--text-primary'),
  textSecondary: getCssVar('--text-secondary'),
  textDisabled: getCssVar('--text-disabled'),
  grey100: getCssVar('--grey-100'),
  grey200: getCssVar('--grey-200'),
  grey300: getCssVar('--grey-300'),
  grey400: getCssVar('--grey-400'),
  grey500: getCssVar('--grey-500'),
  grey600: getCssVar('--grey-600'),
  grey700: getCssVar('--grey-700'),
  grey800: getCssVar('--grey-800'),
  grey900: getCssVar('--grey-900'),
  backgroundDefault: getCssVar('--background-default'),
  backgroundPaper: getCssVar('--background-paper'),
  borderPrimary: getCssVar('--border-primary'),
  divider: getCssVar('--divider'),
  // Chart specific colors
  chart1: getCssVar('--chart-1'),
  chart2: getCssVar('--chart-2'),
  chart3: getCssVar('--chart-3'),
  chart4: getCssVar('--chart-4'),
  chart5: getCssVar('--chart-5'),
  chart6: getCssVar('--chart-6'),
  chart7: getCssVar('--chart-7'),
  chart8: getCssVar('--chart-8'),
  chartTooltipBackground: getCssVar('--chart-tooltip-background'),
  chartTooltipBorder: getCssVar('--chart-tooltip-border'),
  chartTooltipText: getCssVar('--chart-tooltip-text'),
  blueDark: getCssVar('--blue-dark'),
  purpleDark: getCssVar('--purple-dark'),
});

/**
 * Gets chart colors array for consistent theming across all charts
 */
export const getChartColors = (): string[] => {
  const colors = getThemeColors();
  return [
    colors.chart1,
    colors.chart2,
    colors.chart3,
    colors.chart4,
    colors.chart5,
    colors.chart6,
    colors.chart7,
    colors.chart8,
    colors.chartTooltipBackground,
    colors.chartTooltipBorder,
    colors.chartTooltipText,
  ];
};

/**
 * Gets Highcharts theme configuration
 * Use this to apply consistent theming to all Highcharts
 */
export const getHighchartsTheme = () => {
  const colors = getThemeColors();

  return {
    colors: getChartColors(),
    chart: {
      backgroundColor: colors.backgroundPaper,
      style: {
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      },
      plotBorderColor: colors.borderPrimary,
    },
    title: {
      style: {
        color: colors.textPrimary,
      },
    },
    subtitle: {
      style: {
        color: colors.textSecondary,
      },
    },
    xAxis: {
      gridLineColor: colors.divider,
      labels: {
        style: {
          color: colors.textSecondary,
        },
      },
      lineColor: colors.borderPrimary,
      minorGridLineColor: colors.divider,
      tickColor: colors.borderPrimary,
      title: {
        style: {
          color: colors.textPrimary,
        },
      },
    },
    yAxis: {
      gridLineColor: colors.divider,
      labels: {
        style: {
          color: colors.textSecondary,
        },
      },
      lineColor: colors.borderPrimary,
      minorGridLineColor: colors.divider,
      tickColor: colors.borderPrimary,
      title: {
        style: {
          color: colors.textPrimary,
        },
      },
    },
    tooltip: {
      backgroundColor: colors.chartTooltipBackground,
      style: {
        color: colors.chartTooltipText,
      },
      borderColor: colors.chartTooltipBorder,
    },
    legend: {
      backgroundColor: colors.backgroundPaper,
      itemStyle: {
        color: colors.textPrimary,
      },
      itemHoverStyle: {
        color: colors.primary,
      },
      itemHiddenStyle: {
        color: colors.textDisabled,
      },
      title: {
        style: {
          color: colors.textSecondary,
        },
      },
    },
    plotOptions: {
      series: {
        dataLabels: {
          color: colors.textPrimary,
        },
        marker: {
          lineColor: colors.borderPrimary,
        },
      },
    },
    navigation: {
      buttonOptions: {
        symbolStroke: colors.textSecondary,
        theme: {
          fill: colors.backgroundPaper,
          states: {
            hover: {
              fill: colors.grey100,
            },
            select: {
              fill: colors.grey200,
            },
          },
        },
      },
    },
    rangeSelector: {
      buttonTheme: {
        fill: colors.backgroundPaper,
        stroke: colors.borderPrimary,
        style: {
          color: colors.textPrimary,
        },
        states: {
          hover: {
            fill: colors.grey100,
          },
          select: {
            fill: colors.primary,
            style: {
              color: 'white',
            },
          },
        },
      },
      inputBoxBorderColor: colors.borderPrimary,
      inputStyle: {
        backgroundColor: colors.backgroundPaper,
        color: colors.textPrimary,
      },
      labelStyle: {
        color: colors.textSecondary,
      },
    },
    navigator: {
      handles: {
        backgroundColor: colors.grey400,
        borderColor: colors.grey600,
      },
      outlineColor: colors.borderPrimary,
      maskFill: 'rgba(128, 128, 128, 0.1)',
      series: {
        color: colors.primary,
        lineColor: colors.primary,
      },
      xAxis: {
        gridLineColor: colors.divider,
      },
    },
    scrollbar: {
      barBackgroundColor: colors.grey400,
      barBorderColor: colors.grey600,
      buttonArrowColor: colors.textSecondary,
      buttonBackgroundColor: colors.grey200,
      buttonBorderColor: colors.grey400,
      rifleColor: colors.textSecondary,
      trackBackgroundColor: colors.grey100,
      trackBorderColor: colors.grey300,
    },
  };
};
